import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  HeartIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { getBirds, Bird } from '../../api/birds';
import { getChecklistItems, ChecklistItemTemplate, ChecklistTypeKey, normalizeItemStatus } from './checklistTemplates';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface HealthChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (checklistData: any) => Promise<void>;
  birds?: Bird[];
  filteredBirds?: Bird[];
  selectedCage?: string;
  checklistType?: ChecklistTypeKey;
}

type ChecklistItem = ChecklistItemTemplate;

const EMPTY_BIRDS: Bird[] = [];

const HealthChecklistModal: React.FC<HealthChecklistModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  birds = EMPTY_BIRDS,
  filteredBirds,
  selectedCage = '',
  checklistType = 'daily'
}) => {
  const [formData, setFormData] = useState({
    checklistDate: new Date().toISOString().split('T')[0],
    checklistType: checklistType,
    notes: ''
  });
  
  const [selectedBirdIds, setSelectedBirdIds] = useState<string[]>([]);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedBirds, setLoadedBirds] = useState<Bird[]>([]);
  const wasOpenRef = useRef(false);
  const prevChecklistTypeRef = useRef(checklistType);

  const availableBirds = useMemo(() => {
    if (filteredBirds !== undefined) return filteredBirds;
    if (birds.length > 0) return birds;
    return loadedBirds;
  }, [filteredBirds, birds, loadedBirds]);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    if (filteredBirds === undefined && birds.length === 0) {
      loadBirds();
    }

    const justOpened = !wasOpenRef.current;
    const typeChanged = prevChecklistTypeRef.current !== checklistType;

    if (justOpened) {
      setSelectedBirdIds([]);
      setFormData({
        checklistDate: new Date().toISOString().split('T')[0],
        checklistType,
        notes: '',
      });
    } else if (typeChanged) {
      setFormData((prev) => ({ ...prev, checklistType }));
    }

    if (justOpened || typeChanged) {
      setChecklistItems(getChecklistItems(checklistType));
    }

    wasOpenRef.current = true;
    prevChecklistTypeRef.current = checklistType;
  }, [isOpen, birds, filteredBirds, checklistType]);

  const loadBirds = async () => {
    try {
      const response = await getBirds();
      setLoadedBirds(response);
    } catch (error) {
      console.error('Error loading birds:', error);
      setLoadedBirds([]);
    }
  };

  const handleChecklistItemChange = (itemId: string, status: 'good' | 'concern' | 'issue', notes?: string) => {
    setChecklistItems(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, status, notes }
          : item
      )
    );
  };

  const toggleBirdSelection = (birdId: string) => {
    setSelectedBirdIds((prev) =>
      prev.includes(birdId) ? prev.filter((id) => id !== birdId) : [...prev, birdId],
    );
  };

  const selectAllBirds = () => {
    setSelectedBirdIds(availableBirds.map((bird) => bird.id));
  };

  const clearBirdSelection = () => {
    setSelectedBirdIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedBirdIds.length === 0) {
      alert('Please select at least one bird');
      return;
    }

    try {
      setLoading(true);
      await onSubmit({
        birdIds: selectedBirdIds,
        checklistType: formData.checklistType,
        checklistItems: checklistItems.map((item) => ({
          ...item,
          status: normalizeItemStatus(item.status),
        })),
        checklistDate: new Date(formData.checklistDate).toISOString(),
        notes: formData.notes,
        cageNumber: selectedCage || undefined,
      });
      handleClose();
    } catch (error) {
      console.error('Error submitting health checklist:', error);
      alert('Failed to submit health checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      checklistDate: new Date().toISOString().split('T')[0],
      checklistType: checklistType,
      notes: ''
    });
    setSelectedBirdIds([]);
    setChecklistItems([]);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedBirds = availableBirds.filter((bird) => selectedBirdIds.includes(bird.id));
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'concern': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'issue': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default: return <ClockIcon className="h-5 w-5 text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'border-green-200 bg-green-50';
      case 'concern': return 'border-yellow-200 bg-yellow-50';
      case 'issue': return 'border-red-200 bg-red-50';
      default: return 'border-gray-200 bg-white';
    }
  };

  const getOverallStatusColor = () => {
    const hasIssues = checklistItems.some(item => item.status === 'issue');
    const hasConcerns = checklistItems.some(item => item.status === 'concern');
    
    if (hasIssues) return 'text-red-600 bg-red-100';
    if (hasConcerns) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getOverallStatusText = () => {
    const hasIssues = checklistItems.some(item => item.status === 'issue');
    const hasConcerns = checklistItems.some(item => item.status === 'concern');
    
    if (hasIssues) return 'Issues Found - Immediate Attention Required';
    if (hasConcerns) return 'Minor Concerns - Monitor Closely';
    return 'All Good - No Issues Found';
  };

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <HeartIcon className="h-6 w-6 text-red-500 mr-3" />
            {checklistType.charAt(0).toUpperCase() + checklistType.slice(1)} Health Checklist
            {selectedCage ? ` — Cage ${selectedCage}` : ' — All cages'}
          </span>
        }
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-6">
                  {/* Bird Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-slate-700">
                          Select birds * ({selectedBirdIds.length} selected)
                        </label>
                        <div className="space-x-2 text-xs">
                          <button type="button" onClick={selectAllBirds} className="text-cyan-700 hover:underline">
                            All
                          </button>
                          <button type="button" onClick={clearBirdSelection} className="text-slate-500 hover:underline">
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2 space-y-2">
                        {availableBirds.length === 0 ? (
                          <p className="text-sm text-slate-500 p-2">No eligible birds for this cage filter.</p>
                        ) : (
                          availableBirds.map((bird) => (
                            <label key={bird.id} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedBirdIds.includes(bird.id)}
                                onChange={() => toggleBirdSelection(bird.id)}
                              />
                              <span>
                                {bird.bandId} — {bird.species?.commonName || 'Unknown'}
                                {bird.cageNumber ? ` · Cage ${bird.cageNumber}` : ''}
                                <span className="text-slate-500"> ({bird.status})</span>
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="checklistDate" className="block text-sm font-medium text-slate-700 mb-2">
                        Checklist Date *
                      </label>
                      <input
                        type="date"
                        id="checklistDate"
                        name="checklistDate"
                        value={formData.checklistDate}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Selected Bird Info */}
                  {selectedBirds.length > 0 && (
                    <div className="bg-slate-50/80 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-900 mb-2">
                        Selected birds ({selectedBirds.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedBirds.map((bird) => (
                          <span key={bird.id} className="text-xs bg-white border rounded px-2 py-1">
                            {bird.bandId}
                            {bird.cageNumber ? ` · ${bird.cageNumber}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Checklist Items */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-slate-900">Health Assessment Checklist</h4>
                    
                    {checklistItems.map((item) => (
                      <div key={item.id} className={`border rounded-lg p-4 ${getStatusColor(item.status)}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(item.status)}
                              <h5 className="text-sm font-medium text-slate-900">{item.title}</h5>
                              <span className="text-xs text-slate-500 bg-gray-100 px-2 py-1 rounded">{item.category}</span>
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                            
                            {/* Status Buttons */}
                            <div className="mt-3 flex space-x-2">
                              <button
                                type="button"
                                onClick={() => handleChecklistItemChange(item.id, 'good')}
                                className={`inline-flex items-center px-3 py-1 border text-xs font-medium rounded ${
                                  item.status === 'good'
                                    ? 'border-green-300 bg-emerald-100 text-emerald-800'
                                    : 'border-gray-300 bg-white text-slate-700 hover:bg-slate-50/80'
                                }`}
                              >
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                Good
                              </button>
                              <button
                                type="button"
                                onClick={() => handleChecklistItemChange(item.id, 'concern')}
                                className={`inline-flex items-center px-3 py-1 border text-xs font-medium rounded ${
                                  item.status === 'concern'
                                    ? 'border-yellow-300 bg-amber-100 text-amber-800'
                                    : 'border-gray-300 bg-white text-slate-700 hover:bg-slate-50/80'
                                }`}
                              >
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                Concern
                              </button>
                              <button
                                type="button"
                                onClick={() => handleChecklistItemChange(item.id, 'issue')}
                                className={`inline-flex items-center px-3 py-1 border text-xs font-medium rounded ${
                                  item.status === 'issue'
                                    ? 'border-red-300 bg-rose-100 text-rose-800'
                                    : 'border-gray-300 bg-white text-slate-700 hover:bg-slate-50/80'
                                }`}
                              >
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                Issue
                              </button>
                            </div>
                            
                            {/* Notes Field */}
                            {(item.status === 'concern' || item.status === 'issue') && (
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-slate-700 mb-1">
                                  Notes (optional)
                                </label>
                                <textarea
                                  value={item.notes || ''}
                                  onChange={(e) => handleChecklistItemChange(item.id, item.status === 'not_checked' ? 'good' : item.status, e.target.value)}
                                  rows={2}
                                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                                  placeholder="Add notes about this concern or issue..."
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Overall Status Summary */}
                  <div className={`p-4 rounded-lg border ${getOverallStatusColor()}`}>
                    <h4 className="text-sm font-medium mb-2">Overall Health Status</h4>
                    <p className="text-sm">{getOverallStatusText()}</p>
                  </div>

                  {/* General Notes */}
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-2">
                      General Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Any additional notes or observations..."
                    />
                  </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Submit Checklist'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default HealthChecklistModal;
